import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  updateTaskData,
  createTask,
  deleteTask,
  fetchTasks,
} from "../../../../store/features/task/taskSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import {
  fetchStaffsByUser,
  fetchUserById,
} from "../../../../store/features/user/userSlice";
import { fetchResourceAllocations } from "../../../../store/features/resourceAllocation/resourceAllocationSlice";
import {
  fetchWorkPackageById,
  fetchWorkPackages,
} from "../../../../store/features/workPackage/workPackageSlice";

const TaskFormData = ({
  taskInfo = {},
  setTaskInfo,
  setworkPackageById,
  setShowTaskModal,
  showTaskModal,
}) => {
  const dispatch = useDispatch();
  const [managersOptions, setManagersOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedWpId, setSelectedWpId] = useState(null);

  const { resourceAllocations } = useSelector(
    (state) => state.resourceAllocations,
  );
  const {
    usersCreatedBy = [],
    staffsByUser = [],
    managers = [],
  } = useSelector((state) => state.users);
  const { workPackageById, workPackages } = useSelector(
    (state) => state.workPackages,
  );

  // support both possible backend shapes: `deliverables` or `wpAttachments`
  const deliverablesList =
    selectedWpId &&
    (workPackageById?.deliverables || workPackageById?.wpAttachments || []);

  const { role, _id } = useSelector((state) => state.auth);

  const initialValues = {
    title: taskInfo.title || "",
    description: taskInfo.description || "",
    notes: taskInfo.notes || "",
    addNotes: !!taskInfo.notes,
    assignedTo: taskInfo.assignedTo || "",
    assignedBy: taskInfo.assignedBy || "",
    startDate: taskInfo.startDate ? taskInfo.startDate.slice(0, 10) : "",
    endDate: taskInfo.endDate ? taskInfo.endDate.slice(0, 10) : "",
    attachments: taskInfo.attachments || [],
    completionNotes: taskInfo.completionNotes || "",
    approvalStatus: taskInfo.approvalStatus || "pending",
    rejectionReason: taskInfo.rejectionReason || "",
    progressLevel: taskInfo.progressLevel || 0,
    wpId: workPackageById._id || "",
    dlvId: deliverablesList?.[0]?._id || "",
    role,
  };

  const getValidationSchema = (workPackages) =>
    Yup.object().shape({
      title: Yup.string().required("Task title is required"),
      wpId: Yup.string().required("Workpackage name is required"),
      assignedBy: Yup.string().required("Assigned by name is required"),
      assignedTo: Yup.string().required("Assigned to name is required"),
      completionNotes: Yup.string().required("Completion notes is required"),
      description: Yup.string().required("Description is required"),
      startDate: Yup.date()
        .required("Start date is required")
        .when("wpId", (wpId, schema) => {
          if (wpId && workPackages) {
            if (workPackages?.startDate && workPackages?.endDate) {
              const startDateStr =
                typeof workPackages.startDate === "string"
                  ? workPackages.startDate.slice(0, 10)
                  : null;
              const endDateStr =
                typeof workPackages.endDate === "string"
                  ? workPackages.endDate.slice(0, 10)
                  : null;
              return schema
                .test(
                  "is-within-wp-start",
                  `Start date must be on or after workpackage start (${
                    startDateStr || "N/A"
                  })`,
                  function (value) {
                    if (!value) return true;
                    const inputDate = new Date(value);
                    inputDate.setHours(0, 0, 0, 0);
                    const wpStart = new Date(workPackages.startDate);
                    wpStart.setHours(0, 0, 0, 0);
                    return inputDate >= wpStart;
                  },
                )
                .test(
                  "is-within-wp-end",
                  `Start date must be on or before workpackage end (${
                    endDateStr || "N/A"
                  })`,
                  function (value) {
                    if (!value) return true;
                    const inputDate = new Date(value);
                    inputDate.setHours(0, 0, 0, 0);
                    const wpEnd = new Date(workPackages.endDate);
                    wpEnd.setHours(0, 0, 0, 0);
                    return inputDate <= wpEnd;
                  },
                );
            }
          }
          return schema;
        }),

      endDate: Yup.date()
        .required("End date is required")
        .test(
          "is-same-or-after-start",
          "End date must be on or after start date",
          function (value) {
            const { startDate } = this.parent;
            if (!startDate || !value) return true;
            const inputEndDate = new Date(value);
            inputEndDate.setHours(0, 0, 0, 0);
            const inputStartDate = new Date(startDate);
            inputStartDate.setHours(0, 0, 0, 0);
            return inputEndDate >= inputStartDate;
          },
        )
        .when("wpId", (wpId, schema) => {
          if (wpId && workPackages) {
            if (workPackages?.endDate) {
              const endDateStr =
                typeof workPackages.endDate === "string"
                  ? workPackages.endDate.slice(0, 10)
                  : null;
              return schema.test(
                "is-within-wp-end",
                `End date must be on or before workpackage end (${endDateStr || "N/A"})`,
                function (value) {
                  if (!value) return true;
                  const inputDate = new Date(value);
                  inputDate.setHours(0, 0, 0, 0);
                  const wpEnd = new Date(workPackages.endDate);
                  wpEnd.setHours(0, 0, 0, 0);
                  return inputDate <= wpEnd;
                },
              );
            }
          }
          return schema;
        }),
    });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      values.projectId = workPackageById.projectId || taskInfo?.projectId;
      values.assistantSuperAdminId = workPackageById.assistantSuperAdminId;
      values.wpId = workPackageById._id || taskInfo?.wpId;

      // Prepare FormData
      const formData = new FormData();
      for (let key in values) {
        if (key === "attachments" && Array.isArray(values.attachments)) {
          values.attachments.forEach((file) => {
            formData.append("attachments", file);
          });
        } else if (key !== "addNotes") {
          if (key === "notes" && !values.addNotes) {
            continue;
          }
          formData.append(key, values[key]);
        }
      }

      if (taskInfo._id) {
        formData.append("_id", taskInfo._id);
        await dispatch(updateTaskData(formData)).unwrap();
        toast.success("Task updated!");
      } else {
        await dispatch(createTask(formData)).unwrap();
        toast.success("Task created!");
      }

      await dispatch(fetchTasks());
      resetForm();
      setTaskInfo({});
      setShowTaskModal(false);
    } catch (err) {
      toast.error(err?.message || "Error saving Task");
    } finally {
      setSubmitting(false);
    }
  };

  const completionNotesOptions = [
    "Pending",
    "Accepted Needs Changes",
    "Started",
    "Ongoing",
    "Onhold",
    "Accepted partially",
    "Accepted",
  ];

  // Fetch managers and resources
  useEffect(() => {
    if (workPackageById.assistantSuperAdminId) {
      dispatch(fetchStaffsByUser(workPackageById.assistantSuperAdminId));
      dispatch(fetchUserById(_id));
      dispatch(fetchResourceAllocations());
    } else {
      setManagersOptions([]);
    }
    dispatch(fetchWorkPackages());
  }, [
    workPackageById?.project?.assistantSuperAdminId,
    workPackageById.assistantSuperAdminId,
    _id,
    dispatch,
  ]);

  // Compute unique managers from multiple sources
  useEffect(() => {
    if (!workPackageById.assistantSuperAdminId) return;

    const allocation = resourceAllocations.find(
      (ra) =>
        ra.assistantSuperadmin?._id === workPackageById.assistantSuperAdminId,
    );

    const allocStaffs = allocation?.resource?.flatMap((r) => r.staffs) || [];

    const apiStaffs = Array.isArray(staffsByUser)
      ? staffsByUser[0]?.createdUsers || []
      : [];
    const userCreatedBy = Array.isArray(usersCreatedBy)
      ? usersCreatedBy[0]?.createdUsers || []
      : [];

    const combinedStaffs = [...allocStaffs, ...apiStaffs, ...userCreatedBy];
    const uniqueStaffs = Array.from(
      new Map(combinedStaffs.map((m) => [m?._id?.toString(), m])).values(),
    );

    setStaffOptions(uniqueStaffs?.filter((s) => s.role === "staff"));
  }, [
    resourceAllocations,
    usersCreatedBy,
    staffsByUser,
    workPackageById?.project?.assistantSuperAdminId,
    workPackageById.assistantSuperAdminId,
  ]);

  useEffect(() => {
    if (selectedWpId || taskInfo.wpId) {
      dispatch(fetchWorkPackageById(taskInfo.wpId || selectedWpId));
    }
  }, [selectedWpId, dispatch, taskInfo.wpId]);

  return (
    <Modal
      size="lg"
      show={showTaskModal}
      onHide={() => setShowTaskModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{taskInfo._id ? "Update" : "Add"} Task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={getValidationSchema(workPackageById)}
              onSubmit={handleSubmit}
            >
              {({
                values,
                handleChange,
                handleSubmit,
                setFieldValue,
                touched,
                errors,
                isSubmitting,
                resetForm,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Work package <span className="text-danger">*</span>
                        </Form.Label>

                        <Form.Select
                          name="wpId"
                          value={values.wpId}
                          disabled={role === "staff" ? true : false}
                          onChange={(e) => {
                            handleChange(e);
                            setSelectedWpId(e.currentTarget.value);
                          }}
                          isInvalid={touched.wpId && !!errors.wpId}
                        >
                          <option value="">Select Workpackage</option>
                          {workPackages?.map((wp) => (
                            <option key={wp._id} value={wp._id}>
                              {wp.title}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.wpId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    {deliverablesList && deliverablesList.length > 0 && (
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Deliverables <span className="text-danger">*</span>
                          </Form.Label>

                          <Form.Select
                            name="dlvId"
                            value={values.dlvId}
                            onChange={handleChange}
                            disabled={role === "staff" ? true : false}
                          >
                            <option value="">Select Deliverable</option>
                            {deliverablesList?.map((dlv) => (
                              <option key={dlv._id} value={dlv._id}>
                                {dlv.title}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.wpId}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}

                    {/* Task Title */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          disabled={role === "staff" ? true : false}
                          isInvalid={touched.title && errors.title}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.title}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Description */}
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Description <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          as="textarea"
                          name="description"
                          value={values.description}
                          disabled={role === "staff" ? true : false}
                          onChange={handleChange}
                          isInvalid={touched.description && errors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.description}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Assigned By */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Assigned By <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="assignedBy"
                          value={values.assignedBy}
                          onChange={handleChange}
                          disabled={role === "staff" ? true : false}
                          isInvalid={touched.assignedBy && errors.assignedBy}
                        >
                          <option value={""}>Select</option>

                          {workPackageById?.managerIds &&
                            managers
                              ?.filter(
                                (m) => m._id === workPackageById?.managerIds[0],
                              )
                              ?.map((manager) => (
                                <option key={manager._id} value={manager._id}>
                                  {manager.name}
                                </option>
                              ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.assignedBy}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Assigned To <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="assignedTo"
                          value={values.assignedTo}
                          disabled={role === "staff" ? true : false}
                          onChange={handleChange}
                          isInvalid={touched.assignedTo && errors.assignedTo}
                        >
                          <option value="">Select</option>
                          {staffOptions
                            ?.filter((staff) => staff?._id && staff?.name)
                            .map((staff) => (
                              <option key={staff._id} value={staff._id}>
                                {staff.name}
                              </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.assignedTo}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Attachments */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Attachments</Form.Label>
                        <Form.Control
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.currentTarget.files);
                            setFieldValue("attachments", files);
                          }}
                        />
                      </Form.Group>
                    </Col>

                    {/* End Date */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Start Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={values.startDate}
                          onChange={handleChange}
                          disabled={role === "staff" ? true : false}
                          isInvalid={touched.startDate && errors.startDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.startDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* End Date */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          End Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={values.endDate}
                          disabled={role === "staff" ? true : false}
                          onChange={handleChange}
                          isInvalid={touched.endDate && errors.endDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.endDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Status */}
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Completion Notes{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="completionNotes"
                          value={values.completionNotes}
                          onChange={handleChange}
                          isInvalid={
                            touched.completionNotes && errors.completionNotes
                          }
                        >
                          <option value="">Select</option>
                          {completionNotesOptions?.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.completionNotes}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="checkbox"
                        label="Add Notes"
                        name="addNotes"
                        checked={values.addNotes}
                        onChange={(e) =>
                          setFieldValue("addNotes", e.target.checked)
                        }
                        className="mb-2"
                      />
                      {values.addNotes && (
                        <Form.Group className="mb-3">
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="notes"
                            value={values.notes}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-end">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          resetForm();
                          setTaskInfo({});
                          setShowTaskModal(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        className="ms-2"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {taskInfo.title ? "Update" : "Create"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

export default TaskFormData;
